import React from 'react'
import styled from 'styled-components'
import TypographyAtom from '../../atoms/typography/typography'
import Loader from '../../atoms/loader/Loader'
import useStore from '../../../context/StoreContext'
import getFormattedPrice, { getCurrentCurrency } from '../../../helpers/getFormattedPrice'

const Container = styled.div`
  .price-on-request {
    p {
      font-size: 24px;

      @media only screen and (min-width: 48em) {
        font-size: 32px;
      }
    }
  }

  .priceWrapper.rental {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;

    & > div:first-of-type {
      margin-right: 8px;
    }

    & > p {
      font-size: 24px;
    }
  }

  @media only screen and (min-width: 48em) {
    .priceWrapper:not(.rental) {
      & > p:last-child {
        font-size: 18px;
      }
    }
    .priceWrapper.rental {
      p {
        font-size: 32px;
      }
    }
  }

  .priceWrapper:not(.rental) {
    & > p:last-child {
      font-size: 18px;
    }
  }

  p {
    margin: 0;
  }
`

const PriceLabel = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 8px;

  p {
    font-size: 32px;
  }

  p:nth-child(2) {
    text-decoration: line-through;
  }

  p:nth-child(3) {
    font-size: 24px;
  }
`

function PriceTag({
  hidePrice,
  enquireWithPrice,
  rentalConfig,
  selectedVariant,
  isFreeCourseProduct,
  taxIncluded = true,
  isFreeDelivery = true,
  deliveryTextHidden,
}) {
  const { currentMarket } = useStore()

  const currentPriceVariant = selectedVariant.presentmentPrices?.find(
    (item) => item?.price?.currencyCode === getCurrentCurrency({ currentMarket }).currencyCode
  )

  const price = parseFloat(currentPriceVariant?.price?.amount) || 0
  const compareAtPrice = parseFloat(currentPriceVariant?.compareAtPrice?.amount)

  const priceSaving = parseFloat(compareAtPrice - price || 0)
  const isPriceComparingShown = compareAtPrice > 0 && priceSaving > 0
  const isDeliveryTextHidden =
    deliveryTextHidden ||
    [
      'gid://shopify/ProductVariant/43990714941674',
      'gid://shopify/ProductVariant/43977156657386',
      'gid://shopify/ProductVariant/55131779924354',
    ].includes(selectedVariant.shopifyId)

  if (Number.isNaN(price)) {
    return <Loader />
  }

  if (isFreeCourseProduct) {
    return (
      <Container>
        <div className="price-on-request">
          <TypographyAtom type="P" weight="Regular" copy="Free" />
        </div>
      </Container>
    )
  }

  const getDeliveryText = () => {
    let deliveryText = ''
    if (price > 99 && currentMarket === 'GB') {
      if (isFreeDelivery) {
        deliveryText = 'Free delivery'
      }
    } else {
      deliveryText = 'Shipping calculated at checkout'
    }

    if (taxIncluded && currentMarket === 'GB') {
      deliveryText = 'Tax included in UK' + `${deliveryText.length > 0 ? ' & ' : ''}${deliveryText}`
    } else {
      deliveryText =
        'Price shown is ex-VAT' + `${deliveryText.length > 0 ? ' & ' : ''}${deliveryText}`
    }

    if (rentalConfig) deliveryText = currentMarket === 'GB' ? 'inc VAT weekly' : 'ex-VAT weekly'

    return deliveryText
  }

  if (rentalConfig && currentMarket !== 'GB') {
    return (
      <Container>
        <div className="price-on-request">
          <TypographyAtom type="P" weight="Regular" copy="Unavailable" />
        </div>
      </Container>
    )
  }

  return (
    <Container>
      {hidePrice && !enquireWithPrice ? (
        <div className="price-on-request">
          <TypographyAtom type="P" weight="Regular" copy="Price on Request" />
        </div>
      ) : (
        <div className={`priceWrapper ${rentalConfig ? 'rental' : ''}`}>
          <PriceLabel>
            <TypographyAtom
              type="P"
              weight="Regular"
              copy={getFormattedPrice({ currentMarket, price })}
            />
            {isPriceComparingShown && (
              <>
                <TypographyAtom
                  type="P"
                  weight="Regular"
                  color="Grey"
                  copy={getFormattedPrice({
                    currentMarket,
                    price: compareAtPrice,
                  })}
                />
                <TypographyAtom
                  type="P"
                  weight="Regular"
                  color="Orange"
                  copy={`Save ${getFormattedPrice({ currentMarket, price: priceSaving })}`}
                />
              </>
            )}
          </PriceLabel>
          {!isDeliveryTextHidden && (
            <TypographyAtom type="P" weight="Regular" copy={getDeliveryText()} />
          )}
        </div>
      )}
    </Container>
  )
}

export default PriceTag
